/**
 * LAE Productivity (Schools mode): pure date logic for the period controls.
 *
 * Ported from the LAE app (`src/features/productivity/`):
 *   - `format.js`, the date half: `todayIst`, `isIsoDate`, `addDays`, `mondayOf`, `spanDays`;
 *   - `useProductivityParams.js`: `computeRange` (the five presets) and the granularity rule;
 *   - `ProductivityPage.prevParams`: the previous-period window (`previousWindow`);
 *   - `SchoolsView.onBucketClick`: the period a trend-bucket click selects (`bucketTarget`).
 *
 * Every date is a bare `YYYY-MM-DD` India calendar day (Asia/Kolkata). Arithmetic runs in UTC on the
 * date parts (`Date.UTC`), so the browser's own zone never shifts a day. `todayIst` is the only place
 * the clock is read.
 *
 * Deliberate differences from LAE:
 *   - Parsing is strict. LAE's `toUtcMs` accepted a datetime prefix and let `Date.UTC` roll an
 *     impossible day over (`2026-02-30` became 2 March). Here only an exact, real `YYYY-MM-DD` parses;
 *     anything else is "not a date".
 *   - `todayIst` formats with the `en-CA` locale (spec brief), read through `formatToParts`, so the
 *     result never depends on the locale's separator or field order.
 *   - The day stepper's arithmetic (`stepDay`) is not a separate export: it is `addDays(day, n)`,
 *     refused by the caller when the result is after T (P4 owns the URL write).
 *   - No React here.
 */

const DAY_MS = 86_400_000;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const IST_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** A window of India days, both ends inclusive, `from <= to`. */
export type LaeDateRange = { from: string; to: string };

/** Trend bucket size the backend accepts. */
export type LaeGranularity = 'day' | 'week';

/** What a trend-bucket click selects: a custom week window, or one day. */
export type LaeBucketTarget =
  | { preset: 'custom'; from: string; to: string }
  | { preset: 'day'; day: string };

/** `YYYY-MM-DD` → UTC epoch ms of that calendar day, or null when it is not a real date. */
function toUtcMs(iso: unknown): number | null {
  if (typeof iso !== 'string') return null;
  const m = ISO_DATE_RE.exec(iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(0);
  // setUTCFullYear, not Date.UTC: Date.UTC maps years 0-99 onto 1900-1999.
  date.setUTCFullYear(y, mo - 1, d);
  // Reject roll-over (2026-02-30 → 2026-03-02, month 13, day 00).
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return date.getTime();
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

/** UTC epoch ms → `YYYY-MM-DD` from the UTC parts. */
function fromUtcMs(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)}`;
}

/**
 * todayIst(now?: Date): string
 * Today in India as `YYYY-MM-DD`. `now` defaults to the current instant (pass one in tests).
 */
export function todayIst(now: Date = new Date()): string {
  const parts: Record<string, string> = {};
  for (const p of IST_DATE.formatToParts(now)) parts[p.type] = p.value;
  return `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''}`;
}

/**
 * isIsoDate(s: unknown): s is string
 * True only for a real calendar date written exactly `YYYY-MM-DD` (`2026-02-30` is false).
 */
export function isIsoDate(s: unknown): s is string {
  return toUtcMs(s) !== null;
}

/**
 * addDays(iso: string, n: number): string
 * The day `n` days after `iso` (negative = before). An invalid `iso` comes back unchanged, as in LAE.
 */
export function addDays(iso: string, n: number): string {
  const ms = toUtcMs(iso);
  return ms === null ? iso : fromUtcMs(ms + Math.trunc(n) * DAY_MS);
}

/**
 * mondayOf(iso: string): string
 * The Monday on or before `iso` (weeks start on Monday). An invalid `iso` comes back unchanged.
 */
export function mondayOf(iso: string): string {
  const ms = toUtcMs(iso);
  if (ms === null) return iso;
  const dow = new Date(ms).getUTCDay(); // 0 = Sunday
  return fromUtcMs(ms - ((dow + 6) % 7) * DAY_MS);
}

/**
 * spanDays(from: string, to: string): number
 * Inclusive day count, order-insensitive: `spanDays(d, d) === 1`, so the 30-day preset is 30.
 * 0 when either bound is not a real date.
 */
export function spanDays(from: string, to: string): number {
  const a = toUtcMs(from);
  const b = toUtcMs(to);
  if (a === null || b === null) return 0;
  return Math.round(Math.abs(b - a) / DAY_MS) + 1;
}

/**
 * computeRange(preset: string, day: string, from: string | null | undefined,
 *              to: string | null | undefined, T: string): LaeDateRange
 * The window a period preset selects, with `T` = India today:
 *   - `day`    → `day..day` (the caller has already validated `day` and capped it at T);
 *   - `7d`     → `T−6 .. T`;
 *   - `30d`    → `T−29 .. T`;
 *   - `12w`    → `mondayOf(T) − 77 .. T` (11 full weeks plus this week so far);
 *   - `custom` → `from..to`; an invalid bound falls back to the 30-day bound (`from` → T−29, `to` → T),
 *                and reversed bounds are swapped;
 *   - anything else → `T..T` (LAE default branch).
 */
export function computeRange(
  preset: string,
  day: string,
  from: string | null | undefined,
  to: string | null | undefined,
  T: string,
): LaeDateRange {
  switch (preset) {
    case 'day':
      return { from: day, to: day };
    case '7d':
      return { from: addDays(T, -6), to: T };
    case '30d':
      return { from: addDays(T, -29), to: T };
    case '12w':
      return { from: addDays(mondayOf(T), -77), to: T };
    case 'custom': {
      const f = isIsoDate(from) ? from : addDays(T, -29);
      const t = isIsoDate(to) ? to : T;
      // Same-width ISO strings compare correctly as text.
      return f <= t ? { from: f, to: t } : { from: t, to: f };
    }
    default:
      return { from: T, to: T };
  }
}

/**
 * granularityOf(from: string, to: string): LaeGranularity
 * `day` when the inclusive span is at most 31 days, else `week`. (`12w` is weekly; every other preset
 * is daily unless a custom range runs past 31 days.)
 */
export function granularityOf(from: string, to: string): LaeGranularity {
  return spanDays(from, to) <= 31 ? 'day' : 'week';
}

/**
 * previousWindow(from: string, to: string): LaeDateRange | null
 * The equal-length window just before `from..to`: both ends shifted back by the window's own
 * inclusive length `n = spanDays(from, to)` (30d → 30). Not re-aligned to Monday. The caller copies
 * granularity from the current request rather than recomputing it. null when a bound is not a date.
 */
export function previousWindow(from: string, to: string): LaeDateRange | null {
  if (!isIsoDate(from) || !isIsoDate(to)) return null;
  const lo = from <= to ? from : to;
  const hi = from <= to ? to : from;
  const n = spanDays(lo, hi);
  return { from: addDays(lo, -n), to: addDays(hi, -n) };
}

/**
 * bucketTarget(bucket: string, granularity: LaeGranularity, T: string): LaeBucketTarget | null
 * The period a click on a trend bucket selects (LAE `SchoolsView.onBucketClick`):
 *   - weekly: `custom` from the bucket's start through start + 6, capped at T;
 *   - daily: `day` = that bucket.
 * null when `bucket` is not a real date.
 */
export function bucketTarget(bucket: string, granularity: LaeGranularity, T: string): LaeBucketTarget | null {
  if (!isIsoDate(bucket)) return null;
  if (granularity === 'week') {
    const end = addDays(bucket, 6);
    return { preset: 'custom', from: bucket, to: end < T ? end : T };
  }
  return { preset: 'day', day: bucket };
}
